<template lang="html">
  <div :style="`height:${windowHeight}px;width:100%`">
    <v-tooltip
      v-if="params('draft') === 'true'"
    >
      <template v-slot:activator="{ on }">
        <v-btn
          icon
          style="position:absolute;top:8px;left:44px;z-index:1"
          v-on="on"
          @click="bboxPasted()"
        >
          <v-icon>mdi-crop-free</v-icon>
        </v-btn>
      </template>
      <span>Copier la position actuelle</span>
    </v-tooltip>
    <v-progress-linear
      v-if="loading"
      style="position:absolute;z-index:1;max-width:100%;pointer-events: none;"
      indeterminate
      color="primary"
    />
    <div
      id="map"
      style="height:100%"
    />
  </div>
</template>

<script>
  import { bbox2zoom } from '../assets/map-utils'
  import { mapState, mapGetters } from 'vuex'
  import axios from 'axios'

  import 'maplibre-gl/dist/maplibre-gl.css'
  import eventBus from '../assets/event-bus.js'

  const polygonClipping = require('polygon-clipping')
  const fitBoundsOptions = { maxZoom: 16, duration: 0 }
  const style = require('../assets/style')

  const maplibregl = require('maplibre-gl')

  export default {
    data: () => ({
      parcelsRefreshed: false,
      metricAvg: null,
      dataTilesUrl: null,
    }),
    computed: {
      ...mapState(['application', 'dataset', 'loading', 'parcel']),
      ...mapGetters(['datasetUrl', 'tileserverUrl', 'parcelleIdField', 'config', 'defaultColor', 'dateField', 'legend', 'linkField']),
    },
    async mounted () {
      if (this.config.heightField) this.metricAvg = (await axios.get(this.datasetUrl + '/metric_agg', { params: { metric: 'avg', metric_field: this.config.heightField } })).data.metric
      this.initMap()
      eventBus.$on('goTo', (pos, zoom) => {
        this.$store.commit('setAny', { loading: true })
        this.map.setZoom(zoom || 11)
        this.map.setCenter([pos[1], pos[0]])
        setTimeout(() => {
          this.map.fire('click', { lngLat: { lat: pos[0], lng: pos[1] } })
        }, 1000)
      })
      eventBus.$on('query', (query) => {
        const select = [this.parcelleIdField.key]
        if (this.config.colorField) select.push(this.config.colorField)
        if (this.config.heightField) select.push(this.config.heightField)
        if (this.config.baseField) select.push(this.config.baseField)
        if (this.linkField) select.push(this.linkField.key);
        (this.config.tooltip || []).forEach(f => select.push(f))
        const dataTilesUrl = `${this.datasetUrl}/lines?format=pbf&size=10000&sort=_rand&select=${select.join(',') + query}&finalizedAt=${this.config.datasets[0].finalizedAt}&xyz={x},{y},{z}`
        if (this.map.isStyleLoaded()) {
          if (this.map.getSource('data-fair')) {
            this.map.removeLayer('parcelle-dataset-infos')
            this.map.removeLayer('parcelle-dataset-points')
            this.map.removeSource('data-fair')
          }
          this.parcelsRefreshed = false
          this.map.addSource('data-fair', { type: 'vector', tiles: [dataTilesUrl] })
          this.updateDataLayer()
        } else this.dataTilesUrl = dataTilesUrl
      })
      window.addEventListener('resize', () => {
        setTimeout(() => {
          this.map.resize()
        }, 100)
      }, false)
    },
    methods: {
      initMap () {
        if (!maplibregl) return
        const bbox = (this.config.initialBounds && this.config.initialBounds.length) ? JSON.parse(this.config.initialBounds) : this.dataset.bbox
        const initialBounds = maplibregl.LngLatBounds.convert(bbox)
        this.map = new maplibregl.Map({
          container: 'map',
          center: initialBounds ? initialBounds.getCenter().toArray() : [2.0833, 47.2167],
          zoom: initialBounds ? bbox2zoom(bbox) : 7,
          style,
          transformRequest: (url, resourceType) => {
            return {
              url,
              credentials: 'include', // include cookies, for data-fair sessions
            }
          },
        })
        this.map.addControl(new maplibregl.NavigationControl(), 'top-left')
        this.map.addControl(new maplibregl.ScaleControl(), 'bottom-left')

        // Disable map rotation using right click + drag
        this.map.dragRotate.disable()
        // Disable map rotation using touch rotation gesture
        this.map.touchZoomRotate.disableRotation()

        this.map.on('click', (e) => this.showTooltip([e.lngLat.lat, e.lngLat.lng]))

        if (!this.config.initialBounds || !this.config.initialBounds.length) this.map.fitBounds(this.dataset.bbox, fitBoundsOptions)
        if (this.config.heightField) {
          this.setPitch()
          this.map.on('zoom', this.setPitch)
        }
        // Finalize map after first load
        this.map.once('load', () => {
          this.map.setPaintProperty('batiments', 'fill-opacity', 0.4)
          this.map.addSource('data-fair', { type: 'vector', tiles: [this.dataTilesUrl] })
          this.updateDataLayer()
          // Layer for clicked/selected parcelle
          this.map.addLayer({
            id: 'parcelles-selected',
            type: 'line',
            source: 'cadastre',
            'source-layer': 'parcelles',
            layout: {},
            minzoom: 13,
            paint: {
              'line-color': '#FF3D00',
              'line-width': {
                stops: [[12, 0.75], [22, 6]],
              },
            },
            filter: ['in', 'id', ''],
          }, 'poi_label')

          this.map.on('moveend', () => this.moved())
          if (this.config.geolocate) {
            this.geolocateControl.trigger()
          }
        })

        this.map.once('styledata', () => {
          this.initGeolocation()
        })

        // "idle" event seems to be the best indication that the map is fully loaded
        this.map.on('idle', () => {
          if (this.map.getZoom() >= 13 && !this.parcelsRefreshed) {
            const results = {}
            this.map.querySourceFeatures('data-fair', { sourceLayer: 'results' }).forEach(dfP => {
              results[dfP.properties[this.parcelleIdField.key]] = {
                geometries: [],
                properties: dfP.properties,
                geometry: dfP.geometry,
              }
            })

            // add geometries to our results based on the cadastre parcelles layer
            this.map.querySourceFeatures('cadastre', { sourceLayer: 'parcelles' }).forEach(p => {
              const dfP = results[p.properties.id]
              if (!dfP) return
              dfP.geometries.push(p.geometry)
            })

            // merge the geometries that need it and add a feature for each result
            const features = []
            for (const id of Object.keys(results)) {
              var geometry
              if (results[id].geometries.length === 0) {
                geometry = {
                  type: 'Point',
                  coordinates: results[id].geometry,
                }
              } else if (results[id].geometries.length === 1) {
                geometry = results[id].geometries[0]
              } else {
                geometry = {
                  type: 'MultiPolygon',
                  coordinates: polygonClipping.union(...results[id].geometries.map(g => g.coordinates)),
                }
              }
              const feature = { type: 'Feature', geometry, properties: results[id].properties }
              features.push(feature)
            }

            const data = { type: 'FeatureCollection', features }
            if (this.map.getSource('parcelles-dataset')) {
              this.map.getSource('parcelles-dataset').setData(data)
            } else {
              this.map.addSource('parcelles-dataset', { type: 'geojson', data })
              if (this.config.heightField) {
                this.map.addLayer({
                  id: 'parcelles-dataset-shapes',
                  source: 'parcelles-dataset',
                  type: 'fill-extrusion',
                  minzoom: 13,
                  paint: {
                    'fill-extrusion-color': !this.config.colorField ? this.legend[0][1] : [].concat('match', [['to-string', ['get', this.config.colorField]]], ...this.legend.map(l => l.map(e => e.toString())), this.defaultColor),
                    'fill-extrusion-height': ['/', ['get', this.config.heightField], this.metricAvg / 100],
                    'fill-extrusion-base': this.config.baseField ? ['/', ['get', this.config.baseField], this.metricAvg / 100] : 0,
                    'fill-extrusion-opacity': 0.7,
                  },
                }, 'poi_label')
              }
              this.map.addLayer({
                id: 'parcelles-dataset-shapes-base',
                source: 'parcelles-dataset',
                type: 'fill',
                minzoom: 13,
                paint: {
                  'fill-color': !this.config.colorField ? this.legend[0][1] : [].concat('match', [['to-string', ['get', this.config.colorField]]], ...this.legend.map(l => l.map(e => e.toString())), this.defaultColor),
                  'fill-opacity': this.config.heightField ? 0 : 0.7,
                },
              }, 'poi_label')
            }
            this.parcelsRefreshed = true
            this.$store.commit('setAny', { loading: false })
            if (window.triggerCapture && this.map.getSource('data-fair')) setTimeout(() => window.triggerCapture(), 5000)
          } else {
            this.$store.commit('setAny', { loading: false })
            if (window.triggerCapture && this.map.getSource('data-fair')) window.triggerCapture()
          }
        })
      },
      moved () {
        this.$store.commit('setAny', { loading: true })
        this.parcelsRefreshed = false
      },
      showTooltip (pos) {
        this.tooltipPos = pos
        const lngLat = [this.tooltipPos[1], this.tooltipPos[0]]

        if (this.config.tooltip.length) {
          const parcels = this.map.queryRenderedFeatures(this.map.project(lngLat), {
            layers: this.map.getZoom() >= 13 ? ['parcelles-dataset-shapes-base'] : ['parcelle-dataset-points'],
          }).map(p => p.properties)
          if (this.dateField) {
            parcels.sort((a, b) => b[this.dateField.key].localeCompare(a[this.dateField.key]))
          }
          this.$store.commit('setAny', { features: parcels })
        }
        if (this.map.getZoom() >= 13) {
          const parcel = this.map.queryRenderedFeatures(this.map.project(lngLat), {
            layers: ['parcelles'],
          }).pop()
          if (parcel && (!this.parcel || this.parcel.id !== parcel.properties.id)) {
            this.map.setFilter('parcelles-selected', ['in', 'id', parcel.properties.id])
            this.$store.commit('setAny', { parcel: parcel.properties })
          } else {
            this.map.setFilter('parcelles-selected', ['in', 'id', ''])
            this.$store.commit('setAny', { parcel: undefined, features: [] })
          }
        } else {
          this.$store.commit('setAny', { parcel: null })
        }
      },
      initGeolocation () {
        this.geolocateControl = new maplibregl.GeolocateControl({
          showUserLocation: true,
          trackUserLocation: true,
          // positionOptions: {
          //   enableHighAccuracy: true
          // },
          fitBoundsOptions: {
            maxzoom: 15,
            animate: false,
          },
        })
        this.map.addControl(this.geolocateControl, 'top-left')
        this.geolocateControl.on('error', err => {
          console.log('geolocate error', err)
        })
      },
      bboxPasted () {
        const bbox = JSON.stringify([].concat(...this.map.getBounds().toArray()).map(n => n.toFixed(6)))
        navigator.clipboard.writeText(bbox)
        if (window.parent) window.parent.postMessage({ type: 'set-config', content: { field: 'initialBounds', value: bbox } }, '*')
      },
      setPitch () {
        const zoom = this.map.getZoom()
        if (zoom < 12) return this.map.setPitch(0)
        if (zoom > 15) return this.map.setPitch(54)
        this.map.setPitch((zoom - 12) * 18)
      },
      params (p) {
        const params = new URLSearchParams(window.location.search)
        return params.get(p)
      },
      updateDataLayer () {
        this.map.addLayer({
          id: 'parcelle-dataset-infos',
          source: 'data-fair',
          'source-layer': 'results',
          type: 'fill',
          paint: {
            'fill-opacity': 0,
          },
          minzoom: 13,
        })

        const pointsLayer = {
          id: 'parcelle-dataset-points',
          source: 'data-fair',
          'source-layer': 'results',
          type: 'circle',
          maxzoom: 13,
          minzoom: 2,
          paint: {
            'circle-radius': { stops: [[6, 1.5], [24, 16]] },
            'circle-color': !this.config.colorField ? this.legend[0][1] : [].concat('match', [['to-string', ['get', this.config.colorField]]], ...this.legend.map(l => l.map(e => e.toString())), this.defaultColor),
            'circle-opacity': 0.7,
          },
        }
        this.map.addLayer(pointsLayer, 'poi_label')

        this.map.on('mouseenter', 'parcelle-dataset-infos', (e) => {
          this.map.getCanvas().style.cursor = 'pointer'
        })
        this.map.on('mouseleave', 'parcelle-dataset-infos', () => {
          this.map.getCanvas().style.cursor = ''
        })

        if (this.config.tooltip.length) {
          this.map.on('mouseenter', 'parcelle-dataset-points', (e) => {
            this.map.getCanvas().style.cursor = 'pointer'
          })
          this.map.on('mouseleave', 'parcelle-dataset-points', () => {
            this.map.getCanvas().style.cursor = ''
          })
        }
      },
    },
  }
</script>

<style lang="css">
</style>
